<script setup>
import { useAuthStore } from "@/stores";
import { useAuth } from "@websanova/vue-auth/src/v3.js";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue";
import {
  AnnotationIcon,
  UserIcon,
  QuestionMarkCircleIcon,
  CogIcon,
  AdjustmentsIcon,
  LogoutIcon,
} from "@heroicons/vue/outline";
import { computed, inject, ref } from "vue";
import V4SearchUserDialog from "./V4SearchUserDialog.vue";
import { storeToRefs } from "pinia";
import V4SwitchUserDialog from "./V4SwitchUserDialog.vue";

const axios = inject("axios");
const auth = useAuth();
const authStore = useAuthStore();

const { user } = storeToRefs(authStore);

const switchDialogOpen = ref(false);

const profilePicture = computed(() => user.value?.profile_picture);
const initials = computed(() => {
  if (user.value) {
    const names = user.value.name.split(" ");
    return names
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  }
  return "";
});
const isSuperAdmin = computed(() => authStore.isSuperAdmin);
const isAccountManager = computed(() => authStore.isAccountManager);

const redirect = (url) => {
  window.open(url, "_blank");
};

const logout = () => {
  const finish = (url) => {
    auth.logout();
    authStore.$patch({ user: null, token: null });
    localStorage.removeItem("nextUrl");
    window.location.href = url || "/login";
  };

  axios
    .get("/api/okta/logout")
    .then(({ data }) => finish(data))
    .catch(() => finish("/login"));
};
</script>
<template>
  <Menu as="div" class="relative">
    <div>
      <MenuButton
        class="bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
      >
        <span class="sr-only">Open user menu</span>
        <div
          class="inline-flex overflow-hidden relative justify-center items-center w-9 h-9 bg-gray-100 rounded-full"
        >
          <span
            class="font-medium text-sm text-gray-600"
            v-if="!profilePicture"
          >
            {{ initials }}
          </span>
          <img
            class="w-full h-full object-cover"
            v-else
            :src="profilePicture"
            :alt="initials"
          />
        </div>
      </MenuButton>
    </div>
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="z-50 origin-top-right absolute right-0 mt-2 w-64 rounded-xl shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden"
      >
        <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <div
            class="inline-flex overflow-hidden relative justify-center items-center w-10 h-10 bg-gray-100 rounded-full shrink-0"
          >
            <span
              class="font-medium text-sm text-gray-600"
              v-if="!profilePicture"
            >
              {{ initials }}
            </span>
            <img
              class="w-full h-full object-cover"
              v-else
              :src="profilePicture"
              :alt="initials"
            />
          </div>
          <div class="min-w-0">
            <p class="text-sm font-semibold text-gray-900 truncate">
              {{ user?.name }}
            </p>
            <p class="text-xs text-gray-500 truncate">{{ user?.email }}</p>
          </div>
        </div>

        <div class="py-1">
          <MenuItem v-slot="{ active }">
            <router-link
              :to="{ name: 'mynotes' }"
              :class="[
                active ? 'bg-gray-50' : '',
                'flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700',
              ]"
            >
              <AnnotationIcon class="h-4 w-4 text-gray-400" />My comments
            </router-link>
          </MenuItem>
          <MenuItem v-slot="{ active }">
            <a
              @click="redirect('/central/profile')"
              :class="[
                active ? 'bg-gray-50' : '',
                'flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 cursor-pointer',
              ]"
            >
              <UserIcon class="h-4 w-4 text-gray-400" />Profile
            </a>
          </MenuItem>
          <MenuItem v-slot="{ active }">
            <V4SearchUserDialog v-model="switchDialogOpen" :active="active" />
          </MenuItem>
        </div>

        <div
          v-if="isSuperAdmin || isAccountManager"
          class="py-1 border-t border-gray-100"
        >
          <MenuItem v-if="isSuperAdmin" v-slot="{ active }">
            <a
              @click="redirect('/central/preference')"
              :class="[
                active ? 'bg-gray-50' : '',
                'flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 cursor-pointer',
              ]"
            >
              <CogIcon class="h-4 w-4 text-gray-400" />Preference
            </a>
          </MenuItem>
          <MenuItem v-if="isSuperAdmin || isAccountManager" v-slot="{ active }">
            <router-link
              :to="{ name: 'LMS Settings Page' }"
              :class="[
                active ? 'bg-gray-50' : '',
                'flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700',
              ]"
            >
              <AdjustmentsIcon class="h-4 w-4 text-gray-400" />Settings
            </router-link>
          </MenuItem>
        </div>

        <div class="py-1 border-t border-gray-100">
          <MenuItem v-slot="{ active }">
            <a
              @click="redirect('https://docs.revdojo.com/')"
              :class="[
                active ? 'bg-gray-50' : '',
                'flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 cursor-pointer',
              ]"
            >
              <QuestionMarkCircleIcon class="h-4 w-4 text-gray-400" />FAQ
            </a>
          </MenuItem>
          <MenuItem v-slot="{ active }">
            <button
              @click="logout()"
              type="button"
              :class="[
                active ? 'bg-red-50' : '',
                'flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 w-full',
              ]"
            >
              <LogoutIcon class="h-4 w-4" />Sign out
            </button>
          </MenuItem>
        </div>
      </MenuItems>
    </transition>

    <V4SwitchUserDialog v-model="switchDialogOpen" v-if="switchDialogOpen" />
  </Menu>
</template>
